import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, cx, type IconName } from '@whinerp/ui';
import { useT } from '../app/i18n.js';
import { useSession } from '../app/session.js';
import { buildNavigation } from '../app/navigation.js';
import { navLabelKey } from './NavRail.js';
import { Overlay } from './Overlay.js';

/**
 * The command palette (UX-10, G-021), on Cmd/Ctrl+K.
 *
 * **Driven entirely from the keyboard.** The first version listed its commands
 * as buttons, which meant reaching the fourth result took four presses of Tab
 * from the input and running it took Enter on a button - a palette that needed
 * the mouse to be quick. Focus now stays in the input, the arrow keys move a
 * highlight, and Enter runs it. The list is an ARIA listbox with the input as
 * its combobox, so a screen reader announces the highlighted command as it
 * moves.
 *
 * **It runs actions; it does not find records.** Search is `/`, a separate
 * surface, for the reason given in AppShell: one finds records and the other
 * runs actions, and merging them makes both worse. When nothing matches, the
 * palette says so and points at search rather than quietly becoming it.
 *
 * **It only offers what exists.** Navigation comes from the entitlement set, so
 * an area a workspace does not have is not offered. Create commands exist only
 * for records that have a create flow in this app to open; a command that
 * leads to a screen with no form is worse than no command. They are offered
 * on the same terms as the New button on the list they open - the server is
 * what refuses a person who may not create, and the form shows the refusal.
 */

interface Command {
  key: string;
  group: 'create' | 'go';
  label: string;
  hint: string;
  icon: IconName;
  run: () => void;
}

/**
 * How well a command matches, lower is better, or null for no match.
 *
 * Ranked rather than filtered in list order: typing "co" should put Contacts
 * above Accounts-because-it-says-"company", and a palette whose best match is
 * the fifth row sends people back to the mouse.
 */
function rank(command: Command, query: string): number | null {
  if (query === '') return 0;
  const label = command.label.toLowerCase();
  if (label.startsWith(query)) return 0;
  if (label.split(/\s+/).some((word) => word.startsWith(query))) return 1;
  if (label.includes(query)) return 2;
  if (command.hint.toLowerCase().includes(query)) return 3;
  return null;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const { session } = useSession();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);

  // A palette reopened on yesterday's query and a highlight halfway down a
  // list that no longer exists is a palette that needs clearing before use.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  const commands = useMemo<Command[]>(() => {
    const enabled = new Set(
      session.entitlements.filter((entry) => entry.state === 'on').map((entry) => entry.key),
    );
    const navigation = buildNavigation({ enabled, order: session.preferences.navOrder });
    const go = (route: string) => () => {
      navigate(route);
      onClose();
    };

    const create: Command[] = [
      {
        key: 'create:contact',
        group: 'create',
        label: t('palette.create.contact'),
        hint: t('palette.create.contact.hint'),
        icon: 'UserPlus',
        run: go('/contacts?new=1'),
      },
      {
        key: 'create:product',
        group: 'create',
        label: t('palette.create.product'),
        hint: t('palette.create.product.hint'),
        icon: 'Plus',
        run: go('/products/new/edit'),
      },
    ];

    const areas: Command[] = navigation.all.map((area) => ({
      key: `go:${area.key}`,
      group: 'go',
      label: t(navLabelKey(area.key)),
      hint: area.blurb,
      icon: area.icon,
      run: go(area.route),
    }));

    return [...create, ...areas];
  }, [session.entitlements, session.preferences.navOrder, t, navigate, onClose]);

  const needle = query.trim().toLowerCase();
  const shown = useMemo(
    () =>
      commands
        .map((command, order) => ({ command, order, score: rank(command, needle) }))
        .filter((entry): entry is { command: Command; order: number; score: number } => entry.score !== null)
        // Best match first; ties keep their natural order so the list does
        // not reshuffle between two keystrokes that match equally well.
        .sort((a, b) => a.score - b.score || a.order - b.order)
        .map((entry) => entry.command),
    [commands, needle],
  );

  // The highlight follows the list: a narrower query must not leave it
  // pointing past the end, where Enter would do nothing.
  useEffect(() => {
    setActive(0);
  }, [needle]);

  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>(`[data-index="${String(active)}"]`);
    // Optional: not every environment implements it, and a missing scroll is
    // not worth an exception.
    node?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (shown.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => (index + 1) % shown.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => (index - 1 + shown.length) % shown.length);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(shown.length - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      shown[active]?.run();
    }
  };

  const optionId = (index: number): string => `${listId}-option-${String(index)}`;

  return (
    <Overlay open={open} onClose={onClose} label={t('topbar.commandPalette')}>
      <div data-component="CommandPalette" className="flex flex-col">
        <div className="flex items-center gap-2 border-b border-b-rule px-4 py-3">
          <Icon name="Command" decorative className="text-ink-400" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('topbar.commandPalette')}
            aria-label={t('topbar.commandPalette')}
            role="combobox"
            aria-expanded={shown.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={shown.length > 0 ? optionId(active) : undefined}
            data-palette-input
            className="h-input w-full border-0 bg-transparent text-body text-ink outline-none placeholder:text-ink-300"
          />
        </div>

        {shown.length === 0 ? (
          <div className="flex flex-col gap-1 px-4 py-6 text-center" data-palette-empty>
            <p className="text-body text-ink">{t('palette.empty', { values: { query: query.trim() } })}</p>
            {/* Pointed at, not merged in: search is its own surface. */}
            <p className="text-meta text-ink-meta">{t('palette.searchHint')}</p>
          </div>
        ) : (
          <ul id={listId} ref={listRef} role="listbox" className="max-h-reading overflow-y-auto p-2">
            {shown.map((command, index) => {
              const first = index === 0 || shown[index - 1]?.group !== command.group;
              return (
                <li key={command.key} role="presentation">
                  {first ? (
                    <p className="px-2 pb-1 pt-2 text-meta uppercase tracking-wide text-ink-meta" role="presentation">
                      {t(command.group === 'create' ? 'palette.group.create' : 'palette.group.go')}
                    </p>
                  ) : null}
                  <div
                    id={optionId(index)}
                    role="option"
                    aria-selected={index === active}
                    data-index={index}
                    data-palette-command={command.key}
                    data-active={index === active ? 'true' : undefined}
                    // The pointer and the keyboard move one highlight, so
                    // switching between them never shows two.
                    onMouseMove={() => setActive(index)}
                    onClick={() => command.run()}
                    className={cx(
                      'flex w-full cursor-pointer items-center gap-3 rounded-control px-2 py-2 text-start',
                      'text-body text-ink-600',
                      index === active ? 'bg-sunken text-ink' : '',
                    )}
                  >
                    <Icon name={command.icon} size="inline" decorative />
                    <span className="font-medium text-ink">{command.label}</span>
                    <span className="ms-auto truncate text-meta text-ink-meta">{command.hint}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <p className="border-t border-t-rule px-4 py-2 text-meta text-ink-meta" data-palette-keys>
          {t('palette.keys')}
        </p>
      </div>
    </Overlay>
  );
}
